// Phase 22 — group categories by QuickBooks-aligned bucket.
//
// Canonical bucket display order:
//   asset → liability → equity → income → cogs → expense → other
//
// Within each bucket, categories are sorted alphabetically (case-insensitive).

export const BUCKET_ORDER = ['asset', 'liability', 'equity', 'income', 'cogs', 'expense', 'other'];

/**
 * Group categories by bucket in canonical order.
 *
 * @param {Array} categories — list of category docs (must carry `bucket`)
 * @param {Array} buckets    — catalog from `getBuckets()` (with label/color/order)
 * @param {Object} opts
 *   - includeEmpty (bool, default false): keep buckets with 0 categories in the output.
 *   - filter (fn|undefined): predicate applied to each category (e.g. `!c.is_special`).
 * @returns {Array<{bucket,label,color,order,categories}>}
 */
export function groupByBucket(categories, buckets, opts = {}) {
  const { includeEmpty = false, filter } = opts;
  const catalog = (buckets && buckets.length ? buckets : BUCKET_ORDER.map((code, i) => ({ code, label: code, color: '#94A3B8', order: i + 1 })))
    .slice()
    .sort((a, b) => a.order - b.order);

  const src = (filter ? categories.filter(filter) : categories).slice();
  const byBucket = new Map();
  catalog.forEach((b) => byBucket.set(b.code, []));

  src.forEach((c) => {
    const code = c.bucket && byBucket.has(c.bucket) ? c.bucket : 'other';
    if (!byBucket.has(code)) byBucket.set(code, []);
    byBucket.get(code).push(c);
  });

  // Alphabetical (case-insensitive) sort inside each bucket.
  byBucket.forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })));

  return catalog
    .map((b) => ({ ...b, categories: byBucket.get(b.code) || [] }))
    .filter((row) => includeEmpty || row.categories.length > 0);
}
